import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  fetchGetQuery,
  fetchPutQuery,
  fetchPostQuery,
  fetchDeleteQuery,
} from "../api";

//GET data for only partner UM
export function useGetPartnerUM(id) {
  const url = `Projekti/${id}/UMPartnerV2`;
  return useQuery({
    queryKey: ["partner-um", id],
    queryFn: async () => {
      return fetchGetQuery(url);
    },
  });
}

//GET data for other Partners on project
export function useGetOtherPartners(id) {
  const url = `Projekti/${id}/partnerjiV2`;
  return useQuery({
    queryKey: ["other-partners", id],
    queryFn: async () => {
      return fetchGetQuery(url);
    },
  });
}

//GET data for sifrant vloga partnerja
export function useGetSifrantVlogaPartnerja(isEditing) {
  const url = `Sifrant/vloga`;
  return useQuery({
    queryKey: ["sifrant-vloga-partnerja"],
    queryFn: async () => {
      return fetchGetQuery(url);
    },
    enabled: isEditing, // Disable automatic fetching, we will call it manually when needed
  });
}

//GET sifrant partnerjev to add partner to project
export function useGetSifrantPartnerjev(searchTerm) {
  const url = `Partnerji/sifrantV2?search=${searchTerm}`;
  return useQuery({
    queryKey: ["sifrant-partnerjev", searchTerm],
    queryFn: async () => {
      return fetchGetQuery(url);
    },
    enabled: !!searchTerm && searchTerm.length >= 3, // Only run this query if searchTerm is not empty and has at least 3 characters
  });
}

//GET sodelujoče članice UM na projektu
export function useGetClaniceUMNaProjektu(id) {
  const url = `Projekti/${id}/sodelujoce-claniceV2`;
  return useQuery({
    queryKey: ["clanice-um-na-projektu", id],
    queryFn: async () => {
      return fetchGetQuery(url);
    },
  });
}

/* -------------------------------------------- MUTATIONS -------------------------------------------- */

//API for PUT to update project details of UM PARTNER
export function useEditPartnerUM(id) {
  const url = `Projekti/${id}/UMPartnerV2`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return fetchPutQuery(url, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch project splosno details after successful update
      queryClient.invalidateQueries({
        queryKey: ["partner-um", id],
      });
    },
  });
}

//API for PUT to update project details of other partner on project
export function useEditOtherPartner(id) {
  const url = `Projekti/${id}/partnerjiV2`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return fetchPutQuery(url, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch other partners after successful update
      queryClient.invalidateQueries({
        queryKey: ["other-partners", id],
      });
    },
  });
}

//API POST for adding other partner to project
export function useAddOtherPartnerToProject(id) {
  const url = `Projekti/${id}/partnerjiV2`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return fetchPostQuery(url, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch other partners after successful addition
      queryClient.invalidateQueries({
        queryKey: ["other-partners", id],
      });
    },
  });
}

//API POST for adding clanica UM to project
export function useAddClanicaUMToProject(id) {
  const url = `Projekti/${id}/sodelujoce-claniceV2`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return fetchPostQuery(url, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch clanice UM after successful addition
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", id],
      });
    },
  });
}

//API POST for adding sodelujoča oseba to selected clanica
export function useAddSodelujoceOsebaToClanica(idProjekt) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clanicaID, payload }) => {
      if (!clanicaID) {
        throw new Error("ClanicaID is required but was not provided");
      }
      const url = `Projekti/${idProjekt}/sodelujoce-osebe?ClanicaID=${clanicaID}`;
      return fetchPostQuery(url, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch clanice UM after successful addition
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", idProjekt],
      });
    },
  });
}

//API PUT for editing clanica UM on project
export function useEditClanicaUM(id) {
  const url = `Projekti/${id}/sodelujoce-claniceV2`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      return fetchPutQuery(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", id],
      });
    },
  });
}

//API DELETE for deleting partner from project - to be implemented when API is ready
export function useDeletePartnerFromProject(idProjekt, idPartner) {
  const url = `Projekti/${idProjekt}/partnerjiV2/${idPartner}`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return fetchDeleteQuery(url);
    },
    onSuccess: () => {
      // Invalidate and refetch other partners after successful deletion
      queryClient.invalidateQueries({
        queryKey: ["other-partners", idProjekt],
      });
    },
  });
}

//API DELETE for deleting clanica UM from project - to be implemented when API is ready
export function useDeleteClanicaUMFromProject(idProjekt, idClanica) {
  const url = `Projekti/${idProjekt}/sodelujoce-claniceV2/${idClanica}`;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return fetchDeleteQuery(url);
    },
    onSuccess: () => {
      // Invalidate and refetch clanice UM after successful deletion
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", idProjekt],
      });
    },
  });
}

//API PUT for updating TipOsebe and dates of a contact person
export function useUpdateSodelujoceOsebaTipOsebe(idProjekt) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kisID, clanicaID, tipOsebeID, od, do: doDate }) => {
      if (clanicaID === undefined || clanicaID === null) {
        throw new Error("ClanicaID is required for updating sodelujoca oseba.");
      }
      const url = `Projekti/${idProjekt}/sodelujoce-osebe/tip-osebe?KISID=${kisID}&ClanicaID=${clanicaID}`;
      const resolvedTipOsebeID = Number(tipOsebeID);
      const payload = {
        tipOsebeID: resolvedTipOsebeID,
        tipOsebe: resolvedTipOsebeID,
        od,
        do: doDate,
      };
      const response = await fetchPutQuery(url, payload);
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch clanice UM after successful update
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", idProjekt],
      });
    },
  });
}

//API DELETE for deleting sodelujoča oseba from clanica
export function useDeleteSodelujoceOseba(idProjekt) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kisID, clanicaID }) => {
      const url = `Projekti/${idProjekt}/sodelujoce-osebe/${kisID}?ClanicaID=${clanicaID}`;
      return fetchDeleteQuery(url);
    },
    onSuccess: () => {
      // Invalidate and refetch clanice UM after successful deletion
      queryClient.invalidateQueries({
        queryKey: ["clanice-um-na-projektu", idProjekt],
      });
    },
  });
}
